/* Shapes returned by the API. Property names are PascalCase because they come
   straight from SQL Server column names — renaming them in the API would only
   add a mapping layer with nothing to say. */

export type AccessLevel = 'admin' | 'editor' | 'viewer';

/** What this app calls the two levels — see migration 025. */
export type AppRole = 'admin' | 'user';

export interface Me {
  userId: number;
  email: string;
  displayName: string;
  employeeId: number | null;
  isPlatformAdmin: boolean;
  accessLevel: AccessLevel;
  appRole: AppRole;
  /** On the leadership team — gates the Vision and People sections. */
  isLeadership: boolean;
  /** Codes of the dashboard cards, and therefore the modules, this person may open. */
  areaCodes: string[];
  /** The pages this person may open, by key — see lib/pages.ts and migration
   *  144. Every page for an admin. */
  pages: string[];
}

export interface EmployeeAccess {
  accessLevel: AppRole;
  areas: { AreaId: number; Code: string; Name: string; Granted: boolean }[];
  /** Page keys ticked on the record (migration 144). */
  pages: string[];
}

export interface BusinessUnit {
  BusinessUnitId: number;
  Name: string;
  Description: string | null;
  /** Which department the unit sits under. Many units to one department. */
  DepartmentId: number | null;
  DepartmentName?: string | null;
  SortOrder: number;
  IsActive: boolean;
  EmployeeCount?: number;
}

export interface Department {
  DepartmentId: number;
  Name: string;
  Description: string | null;
  ParentDepartmentId: number | null;
  SortOrder: number;
  IsActive: boolean;
  EmployeeCount?: number;
}

/**
 * What POST /roles returns: the role, plus what was set up alongside it.
 *
 * `Provisioned` is null when the role already had a training row — see
 * lib/rolePack.ts on the server.
 */
export interface RoleCreated extends Role {
  Provisioned: {
    TemplateId: number;
    TrainingRoleId: number;
    RecruitingQuestions: number;
    SampleLabel: string;
    SampleMatched: boolean;
  } | null;
}

export interface Role {
  RoleId: number;
  Title: string;
  Description: string | null;
  DepartmentId: number | null;
  DepartmentName?: string | null;
  IsLeadership: boolean;
  IsActive: boolean;
  EmployeeCount?: number;
}

export interface Employee {
  EmployeeId: number;
  FirstName: string;
  LastName: string;
  FullName: string;
  Email: string | null;
  DepartmentId: number | null;
  DepartmentName: string | null;
  RoleId: number | null;
  RoleTitle: string | null;
  /** The person's own leadership tag — see migration 029. */
  IsLeadership: boolean | null;
  /** Whether their ROLE is a leadership role. A different question. */
  RoleIsLeadership?: boolean | null;
  ManagerEmployeeId: number | null;
  ManagerName: string | null;
  EmploymentType: 'full_time' | 'part_time' | 'contractor' | 'intern';
  Status: 'active' | 'on_leave' | 'terminated';
  StartDate: string | null;
  EndDate: string | null;
}

/* --- Dashboard ----------------------------------------------------------- */

export interface BusinessArea {
  AreaId: number;
  Code: string;
  Name: string;
  Description: string | null;
  AppKey: string | null;
  AppUrl: string | null;
  SortOrder: number;
  DepartmentCount: number;
  Headcount: number;
  OpenRocks: number;
  OffTrackRocks: number;
  OpenIssues: number;
  GwcConcerns: number;
  /** Null when this area has nothing tracked on the dependency map. */
  TopDependencyRisk: number | null;
  CriticalDependencies: number;
  AppRegistered: boolean;
}

export interface DashboardSummary {
  ActiveEmployees: number;
  OpenRocks: number;
  OffTrackRocks: number;
  OpenIssues: number;
  VacantSeats: number;
  CriticalDependencies: number;
  /** Work with no owner — invisible on the tiles, so called out separately. */
  UnownedRocks: number;
  UnownedIssues: number;
  /** Active people whose department is not mapped to any area. */
  UnmappedPeople: number;
  LatestAssessmentId: number | null;
  /** The newest COMPLETED OML assessment's weighted overall, 1-5, or null when
   *  none is finished. On this endpoint rather than /eos/summary so that
   *  everyone signed in can see where the business stands — see the note on
   *  the query. */
  OmlScore: number | null;
  OmlConductedOn: string | null;
}

/* --- Account management --------------------------------------------------- */

export interface AmCostLine {
  priority: 'Critical' | 'High' | 'Medium' | 'Low';
  cost: number;
  costType: 'One-time' | 'Monthly' | 'Annual';
}

export interface AmClientRow {
  AmClientId: string;
  Name: string;
  Industry: string | null;
  Status: string | null;
  MonthlyRevenue: number | null;
  ContractType: string | null;
  HealthScore: number | null;
  PrimaryContact: string | null;
  Email: string | null;
  Phone: string | null;
  /* Stored as an employee id; AccountManager is the name the API resolves from
     core.Employee for display and for grouping the pipeline. */
  AccountManagerEmployeeId: number | null;
  AccountManager: string | null;
  Stakeholders: number;
  Audits: number;
  OpenRecommendations: number;
  NextReviewDate: string | null;
  NextReviewCadence: string | null;
  ReviewOverdue: boolean;
  /** Any review on record, completed included — the summary's coverage test. */
  HasReview: boolean;
  HighRisks: number;
  Products: number;
}

export interface AmStakeholder {
  AmStakeholderId: string;
  Name: string;
  Role: string | null;
  Email: string | null;
  Phone: string | null;
  IsPrimary: boolean;
  IncludeInSbr: boolean;
  /** The relationship record — what makes the next review a conversation. */
  Birthday: string | null;
  Spouse: string | null;
  Kids: string | null;
  Interests: string | null;
  LifeEvents: string | null;
  Notes: string | null;
}

/**
 * A client's compliance assessment as account management sees it — the
 * headline numbers only, without the requirements behind them.
 *
 * Archived ones never appear: the API filters them out, because a retired
 * posture in front of a client is worse than showing nothing. See migration
 * 080 and `lib/complianceReport.ts`.
 */
export interface AmComplianceSummary {
  AssessmentId: number;
  FrameworkId: number;
  FrameworkCode: string;
  FrameworkName: string;
  FrameworkShortName: string | null;
  Category: ComplianceCategory;
  Status: 'in_progress' | 'complete';
  ScopeLevelKey: string;
  ScopeLevelLabel: string | null;
  StartedOn: string | null;
  CompletedOn: string | null;
  OwnerName: string | null;
  InScope: number;
  Met: number;
  Partial: number;
  NotMet: number;
  NotApplicable: number;
  NotAssessed: number;
  Applicable: number;
  /** Met against what applies. Null when nothing applies at this scope level. */
  ScorePct: number | null;
}

export interface AmSbr {
  AmSbrId: string;
  /** Marked complete — the review was held (migration 166). Never overdue. */
  CompletedUtc?: string | null;
  CompletedBy?: string | null;
  Title: string | null;
  Cadence: string | null;
  NextDate: string | null;
  Agenda: string | null;
  Notes: string | null;
  /** JSON id arrays, exactly as the source app stored them. Parse with
   *  `parseIdList` — a malformed value must not take the page down. */
  StakeholderIds: string | null;
  AuditIds: string | null;
  RecIds: string | null;
  /** Compliance assessments this review covers. Numbers, unlike the three
   *  above — see migration 080. Null means the review predates the column and
   *  nothing has been chosen yet. */
  ComplianceIds: string | null;
  /** The "What's new" slide, as JSON — parse with `parseWhatsNew` in
   *  lib/whatsNew.ts. Null when the review has nothing new to say. */
  WhatsNew?: string | null;
  /** The "Business Discussion" slide, as JSON — parse with
   *  `parseDiscussion` in lib/businessDiscussion.ts. Null when empty. */
  BusinessDiscussion?: string | null;
  /** The compliance assessment the Security Maturity slide shows. Null means
   *  no slide — see migration 134. */
  SecurityAssessmentId?: number | null;
  /** The review's own action items, as JSON — parse with `parseActionItems`
   *  in lib/actionItems.ts. Null when none. See migration 135. */
  ActionItems?: string | null;
  /** The Technical Support slide's takeaways, as a JSON array of strings.
   *  Null when none — see migration 136. */
  SupportTakeaways?: string | null;
  /** Whether each Technical Support slide is shown — see migration 137. */
  ShowSupportRequests?: boolean;
  /** Whether the User Feedback slide is shown (migration 153). */
  ShowUserFeedback?: boolean;
  /** Whether the Services Provided slide is shown (migration 153). */
  ShowServicesProvided?: boolean;
  /** Whether the Active IT Projects slide is shown (migration 153). */
  ShowActiveProjects?: boolean;
  /** Whether the Asset Management slides are shown (migration 154). */
  ShowAssetManagement?: boolean;
  /** Whether the Agent Health slide is shown (migration 186). */
  ShowAgentHealth?: boolean;
  ShowTicketSummary?: boolean;
  /** Which slides have an uploaded screenshot, with titles, as JSON — parse
   *  with `parseSlideImages` in lib/slideImages.ts. See migration 138. */
  SlideImages?: string | null;
  /** The Service Information slide's talking points, as a JSON array of
   *  strings. Null when none — see migration 139. */
  ServiceTalkingPoints?: string | null;
  /** The Backup Review slide's description — see migration 140. */
  BackupDescription?: string | null;
  /** The Backup Review slide's takeaways, as a JSON array — see migration 149. */
  BackupTakeaways?: string | null;
  /** The Technical Support slides the review added itself, as JSON — parse
   *  with `parseSupportSlides` in lib/supportSlides.ts. See migration 202. */
  SupportSlides?: string | null;
  /** The thank-you slide's contact lines — see migration 148. */
  ClosingEmail?: string | null;
  ClosingPhone?: string | null;
  ClosingWebsite?: string | null;
}

export interface AmAuditRow {
  AmAuditId: string;
  Name: string | null;
  SetName: string | null;
  AuditDate: string | null;
  Status: string | null;
  Auditor: string | null;
  Summary: string | null;
  AnswerCount: number;
  QuestionCount: number;
  CriticalCount: number;
  HighCount: number;
  MediumCount: number;
  LowCount: number;
}

export interface AmRecommendation {
  AmRecommendationId: string;
  AmClientId: string | null;
  AmAuditId: string | null;
  AmQuestionId: string | null;
  Title: string | null;
  Finding: string | null;
  Description: string | null;
  /** Derived from the cost lines: the most severe of them. */
  Priority: string | null;
  /** Derived from the cost lines: the sum. */
  Cost: number | null;
  CostType: string | null;
  /** The real pricing. One recommendation can carry a one-time build cost and
   *  a monthly run cost at the same time. */
  CostLines: AmCostLine[];
  Effort: string | null;
  Hours: number | null;
  Status: string | null;
  /** Catalogue items this recommendation is proposing. Priced when they were
   *  attached, not looked up live — a quote must not move under the client. */
  Products?: RecommendationProduct[];
  /** The year it is planned in on the Budget tab; null when it is not in the budget (migration 194). */
  BudgetYear?: number | null;
}

/**
 * A recommendation kept to start from — migration 190. Its products carry the
 * catalogue's name and price as of today, not as of when it was saved.
 */
export interface AmRecommendationTemplate {
  AmRecommendationTemplateId: number;
  Name: string;
  Title: string;
  Finding: string | null;
  Description: string | null;
  CostLines: AmCostLine[];
  Products: {
    ProductId: number;
    Quantity: number;
    Name: string;
    UnitPrice: number;
    PeriodType: ProductPeriod;
    Vendor: string | null;
  }[];
  Effort: string | null;
  Hours: number | null;
  CreatedBy: string | null;
  CreatedUtc: string;
  UpdatedBy: string | null;
  UpdatedUtc: string | null;
}

/* --- The product catalogue --------------------------------------------- */

export type ProductPeriod = 'Monthly' | 'Yearly' | 'One-time';

/**
 * A company we buy from, in the shared directory.
 *
 * Identity only — each module's opinions about a vendor (portal URLs, spend,
 * payment terms) stay in that module's own tables, keyed on VendorId. See
 * migration 097.
 */
export interface Vendor {
  VendorId: number;
  Name: string;
  Website: string | null;
  IsActive: boolean;
  ProductCount: number;
  CsVendorCount: number;
}

export interface Product {
  ProductId: number;
  Name: string;
  Description: string | null;
  VendorId: number | null;
  /** The vendor's name, joined from core.Vendor for display. */
  Vendor: string | null;
  CategoryId: number | null;
  /** The category's name, joined for display. */
  Category: string | null;
  PeriodType: ProductPeriod;
  /** Signed — the monthly discount line is a real product priced below zero. */
  UnitPrice: number;
  /** What it costs us per unit, from Autotask's catalogue (migration 152). */
  UnitCost?: number | null;
  /** The export's own doubt, kept: "is this still in use?" with no answer. */
  NeedsReview: boolean;
  IsActive: boolean;
  CreatedUtc: string;
  ModifiedUtc: string;
  TimesRecommended?: number;
}

export interface ProductDetail extends Product {
  recommendations: {
    AmRecommendationId: string;
    Title: string | null;
    Status: string | null;
    Priority: string | null;
    Quantity: number;
    UnitPrice: number;
    PeriodType: ProductPeriod;
    ClientName: string | null;
  }[];
}

export interface ProductSummary {
  Total: number;
  Active: number;
  NeedsReview: number;
  /** Priced at zero — mostly the "work with Jeff to price" placeholders. */
  Unpriced: number;
  /** Filed under nothing, and therefore on no client's product matrix. */
  Uncategorised: number;
  Monthly: number;
  Vendors: number;
  categories: { CategoryId: number; Category: string; SortOrder: number; Count: number }[];
  vendors: { Vendor: string; Count: number }[];
}

/** A catalogue item as attached to a recommendation. */
export interface RecommendationProduct {
  ProductId: number;
  Name: string;
  Vendor: string | null;
  Category: string | null;
  Quantity: number;
  UnitPrice: number;
  PeriodType: ProductPeriod;
  Notes: string | null;
  /** False once the product has been retired from the catalogue. The quote
   *  still stands; the row is just no longer sellable. */
  IsActive: boolean;
}

/** A recommendation in the cross-client pipeline, with its client attached. */
export interface AmPipelineRec extends AmRecommendation {
  ClientName: string | null;
  ResolvedClientId: string | null;
  AccountManager: string | null;
  ClientStatus: string | null;
  AuditName: string | null;
  AuditSetName: string | null;
}

export interface AmClientDetail {
  AmClientId: string;
  Name: string;
  Industry: string | null;
  Website: string | null;
  AccountManagerEmployeeId: number | null;
  AccountManager: string | null;
  PrimaryContact: string | null;
  Email: string | null;
  Phone: string | null;
  Address: string | null;
  Status: string | null;
  MonthlyRevenue: number | null;
  ContractType: string | null;
  HealthScore: number | null;
  Notes: string | null;
  /** Columns from whatever spreadsheet the client list was imported from. */
  CustomFields: Record<string, string>;
  stakeholders: AmStakeholder[];
  sbrs: AmSbr[];
  audits: AmAuditRow[];
  recommendations: AmRecommendation[];
  /** The product matrix: which price-book categories this client buys. */
  productCategories: AmClientCategory[];
}

export interface AmAuditAnswer {
  AmQuestionId: string;
  Category: string | null;
  Text: string;
  Guidance: string | null;
  QuestionType: string | null;
  IsCustom: boolean;
  SortOrder: number;
  /** Null when the question was never answered — not the same as a low risk. */
  AmAnswerId: string | null;
  Response: string | null;
  Risk: string | null;
  Notes: string | null;
}

export interface AmAuditDetail {
  AmAuditId: string;
  AmClientId: string;
  ClientName: string;
  Name: string | null;
  SetName: string | null;
  AuditDate: string | null;
  Status: string | null;
  Auditor: string | null;
  Summary: string | null;
  /** Sections switched off for this client, as a JSON array of category
   *  names — see migration 143. Null when every section applies. */
  ExcludedCategories?: string | null;
  answers: AmAuditAnswer[];
  /** What this audit produced, so a finding can say it already has one and
   *  the recommendation can be read without leaving the audit. */
  recommendations: AmRecommendation[];
}

export interface AmScheduleRow {
  AmSbrId: string;
  AmClientId: string;
  ClientName: string;
  Title: string | null;
  Cadence: string | null;
  NextDate: string | null;
  Agenda: string | null;
  Notes: string | null;
  StakeholderIds: string | null;
  AuditIds: string | null;
  RecIds: string | null;
  HealthScore: number | null;
  MonthlyRevenue: number | null;
  AccountManager: string | null;
  IsOverdue: boolean;
  DaysAway: number | null;
  CompletedUtc: string | null;
  CompletedBy: string | null;
}

export interface AmQuestionSet {
  SetName: string;
  IsDefault: boolean;
  Questions: number;
  Categories: number;
  CustomQuestions: number;
  /** Audits already run against this set. Non-zero means it cannot be deleted. */
  Audits: number;
}

export interface AmQuestion {
  AmQuestionId: string;
  SetName: string | null;
  Category: string | null;
  Text: string;
  Guidance: string | null;
  QuestionType: string | null;
  IsCustom: boolean;
  SortOrder: number;
}

/** A row of the client product matrix — a category of the price book. */
export interface ProductCategoryRow {
  CategoryId: number;
  Name: string;
  SortOrder: number;
  ProductCount: number;
}

/** A category this client buys, and how it came to be ticked. */
export interface AmClientCategory {
  CategoryId: number;
  Name: string;
  /** `recommendation` means an accepted recommendation put it there. When a
   *  tick is wrong, that is the first thing anybody needs to know. */
  Source: 'manual' | 'recommendation';
  FromRecommendationId: string | null;
}

export interface AmProductMatrix {
  categories: { CategoryId: number; Name: string; SortOrder: number }[];
  clients: {
    AmClientId: string;
    Name: string;
    Industry: string | null;
    Status: string | null;
    MonthlyRevenue: number | null;
    AccountManager: string | null;
    categoryIds: number[];
  }[];
}

/** A client taken off the books, and what went quiet with them. */
export interface ClientDeactivation {
  DeactivationId: number;
  Reason: string | null;
  DeactivatedUtc: string;
  ReactivatedUtc: string | null;
  DeactivatedBy: string | null;
  ReactivatedBy: string | null;
  /** JSON: [{table, key, column, from}] — exactly what reactivating puts back. */
  Changes: string | null;
}

/** One row of the "this is what will be switched off" preview. */
export interface DeactivationChange {
  table: string;
  key: string | number;
  column: string;
  from: string | number | null;
}

export interface AmSopMeta {
  sopNumber?: string;
  version?: string;
  effectiveDate?: string;
  reviewDate?: string;
  department?: string;
  author?: string;
  approver?: string;
  purpose?: string;
  scope?: string;
  responsibilities?: string;
}

/**
 * A procedure in the library: either an uploaded document or a pointer at a
 * section of the operations manual. Never both — see migration 184.
 *
 * `ManualSectionId` is the discriminator. When it is set, `Size` is null and
 * the row opens the manual at `SectionSlug` instead of the hosted HTML page.
 */
export interface AmSop {
  AmSopId: string;
  Title: string;
  FileName: string | null;
  Meta: AmSopMeta;
  CreatedUtc: string;
  ModifiedUtc: string;
  /** Characters of stored HTML, for the size column. Null for a linked one. */
  Size: number | null;
  ManualSectionId: number | null;
  SectionCode: string | null;
  SectionTitle: string | null;
  SectionSlug: string | null;
  /** The section it points at has been retired out of the manual's contents. */
  SectionRetired: boolean;
}

/* --- scopes of work ------------------------------------------------------ */

/** Where a scope has got to with the client. Three states and no more: it is
 *  waiting on somebody, it has been agreed, or it is done with. */
export type AmSowStatus = 'Needs approval' | 'Approved' | 'Closed';

/** A row of the saved-scopes list. The document itself is not on it — see the
 *  note on GET /am/sows. */
export interface AmSowRow {
  AmSowId: string;
  AmClientId: string;
  ClientName: string;
  /** False when the client has since been taken off the books. The scope is
   *  still listed; it is simply marked. */
  ClientIsActive: boolean;
  Title: string;
  Status: AmSowStatus;
  Stakeholder: string | null;
  HourlyRate: number | null;
  TotalHours: number;
  Products: number;
  CreatedUtc: string;
  ModifiedUtc: string;
}

export interface AmSowTask {
  name: string;
  description: string;
  hours: number;
}

/**
 * A task in the reusable library the builder picks from.
 *
 * Copied into a scope when picked, never referenced — editing one here does
 * not rewrite a scope that already went to a client.
 */
export interface AmSowLibraryTask {
  AmSowTaskId: string;
  /** The work area it sits under. One of the builder's own area keys. */
  AreaKey: string;
  Name: string;
  Description: string | null;
  /** What it usually takes, offered as the starting figure. Null where no
   *  usual size has been agreed. */
  DefaultHours: number | null;
  SortOrder: number;
  CreatedUtc: string;
  ModifiedUtc: string;
}

/** One work area's tasks. `key` is a work-area key the builder knows; one it
 *  does not is kept by the server and ignored here. */
export interface AmSowArea {
  key: string;
  tasks: AmSowTask[];
}

/** The price and period are what the catalogue said when the scope was
 *  written; the name and vendor are what it says now. */
export interface AmSowProduct {
  ProductId: number;
  Name: string;
  Vendor: string | null;
  IsActive: boolean;
  Quantity: number;
  UnitPrice: number;
  PeriodType: ProductPeriod;
}

/** A product typed in on a scope because the catalogue does not carry it. */
export interface AmSowCustomProduct {
  AmSowCustomProductId: number;
  Name: string;
  Vendor: string | null;
  Quantity: number;
  UnitPrice: number;
  PeriodType: ProductPeriod;
}

/** A saved scope template, as the picker lists it. */
export interface AmSowTemplateRow {
  AmSowTemplateId: string;
  Name: string;
  TotalHours: number;
  HourlyRate: number | null;
  ProductCount: number;
  CreatedBy: string | null;
  CreatedUtc: string;
  ModifiedUtc: string;
}

/** One template, whole. Catalogue products are priced as the catalogue
 *  stands today. */
export interface AmSowTemplateDetail {
  AmSowTemplateId: string;
  Name: string;
  Purpose: string | null;
  HourlyRate: number | null;
  Areas: AmSowArea[];
  Products: AmSowProduct[];
  CustomProducts: AmSowCustomProduct[];
}

/** One saved scope, whole — what the builder re-opens into. */
export interface AmSowDetail extends Omit<AmSowRow, 'Products'> {
  Purpose: string | null;
  Areas: AmSowArea[];
  Products: AmSowProduct[];
  CustomProducts?: AmSowCustomProduct[];
}

/** Review coverage for one account manager — see /am/summary. */
export interface AmCoverageByManager {
  Manager: string;
  Clients: number;
  Booked: number;
}

export interface AmSummary {
  Clients: number;
  /** Worst gap first. Covers the same active clients the figures above do. */
  CoverageByManager: AmCoverageByManager[];
  TotalMrr: number;
  AverageHealth: number | null;
  ReviewsOverdue: number;
  ReviewsDueSoon: number;
  /** Clients with nothing booked — invisible on the schedule by definition. */
  ClientsWithoutReview: number;
  Audits: number;
  HighRisks: number;
  OpenRecommendations: number;
  /** Split, because a monthly recommendation and a one-off are not the same
   *  unit and summing them produces a number that means nothing. */
  OpenRecommendationMonthly: number;
  OpenRecommendationAnnual: number;
  OpenRecommendationOneOff: number;
  /** The delivery side of the same pipeline — what the open recommendations
   *  would cost in labour if every one of them were said yes to. */
  OpenRecommendationHours: number;
  /** Clients carrying the "At Risk" status, and the MRR that leaves with them. */
  AtRiskClients: number;
  AtRiskMrr: number;
  Sops: number;
  /** The operating company. Client-facing documents are signed with it. */
  OrganizationName: string;
}

/** The JSON id arrays on a review. Malformed JSON reads as "nothing selected"
 *  rather than throwing — one bad row must not take the schedule down. */
export function parseIdList(value: string | null | undefined): string[] {
  if (!value) return [];
  try {
    const parsed: unknown = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

/**
 * The same, for a list whose ids are numbers.
 *
 * `parseIdList` filters to strings, which is right for the three id arrays a
 * review has always carried — they hold the source app's NVARCHAR(24) keys.
 * `ComplianceIds` holds integer primary keys instead (migration 080), and
 * running those through the string parser silently returns an empty list,
 * which is a compliance slide that never appears and nothing to say why.
 *
 * Numeric strings are accepted too. Whether an id survives a round trip as
 * `2` or `"2"` depends on how it was written, and that is not a distinction
 * worth losing a slide over.
 */
export function parseNumberIdList(value: string | null | undefined): number[] {
  if (!value) return [];
  try {
    const parsed: unknown = JSON.parse(value);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((v) => (typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN))
      .filter((n) => Number.isInteger(n) && n > 0);
  } catch {
    return [];
  }
}

/* --- Operations manual ---------------------------------------------------- */

export interface ManualSection {
  SectionId: number;
  Code: string | null;
  Title: string;
  Slug: string;
  ParentSectionId: number | null;
  SortOrder: number;
  VersionNumber: number | null;
  UpdatedUtc: string | null;
  Author: string | null;
  DocumentCount: number;
  VersionCount: number;
}

export interface ManualDocument {
  DocumentId: number;
  Filename: string;
  /**
   * Relative to the manual asset root, exactly as the source app stored it.
   * Null on an uploaded document, whose bytes are in the database instead —
   * see migration 112. Exactly one of RelPath and the stored content is set.
   */
  RelPath: string | null;
  Extension: string | null;
  SizeBytes: number;
  MimeType: string | null;
  UploadedUtc: string | null;
  UploadedBy: string | null;
  /** False for the documents that came across with the import. */
  IsUploaded: boolean;
}

export interface ManualVersionRow {
  VersionId: number;
  VersionNumber: number;
  Title: string;
  Note: string | null;
  Author: string | null;
  IsCurrent: boolean;
  CreatedUtc: string;
}

export interface ManualSectionDetail {
  SectionId: number;
  Code: string | null;
  Title: string;
  Slug: string;
  ParentSectionId: number | null;
  ParentTitle: string | null;
  ParentSlug: string | null;
  VersionId: number | null;
  VersionNumber: number | null;
  BodyHtml: string | null;
  Note: string | null;
  Author: string | null;
  UpdatedUtc: string | null;
  documents: ManualDocument[];
  history: ManualVersionRow[];
}

export interface ManualSearchHit {
  SectionId: number;
  Code: string | null;
  Title: string;
  Slug: string;
  Snippet: string | null;
}

/**
 * One PSA workflow rule — a tab of Workflow Mapping.xlsx, as section 1C now
 * holds it. See migration 096 for what came across and what did not.
 */
export interface WorkflowRule {
  WorkflowRuleId: number;
  /** WF01 … WF87, the workbook's own handle. */
  Code: string;
  Name: string;
  /** Free text, comma-separated, as the source classified it. */
  Kinds: string | null;
  SourceRow: number | null;
  TriggerText: string | null;
  ConditionText: string | null;
  ActionText: string | null;
  EndText: string | null;
  TemplateName: string | null;
  SentTo: string | null;
  Audience: string | null;
  TemplateDetails: string | null;
  PsaCreatedOn: string | null;
  PsaCreatedBy: string | null;
  PsaUpdatedOn: string | null;
  PsaUpdatedBy: string | null;
  Associated: string | null;
  /**
   * Whether there is a screenshot of the e-mail this rule sends. The filename
   * stays on the server; the bytes come from
   * `/manual/workflow-rules/{Code}/screenshot`.
   */
  HasScreenshot: boolean;
  /** The questions written in the workbook's margins. Often the point. */
  ReviewNotes: string[];
}

/* --- Service delivery ----------------------------------------------------- */

export interface ServicePeriod {
  ScorecardId: number;
  PeriodLabel: string;
  PeriodStart: string;
  PeriodEnd: string | null;
  /** Null means "not measured this period", which is not the same as zero. */
  CsatPercent: number | null;
  BillablePercent: number | null;
  SlaResponsePercent: number | null;
  SlaAcknowledgePercent: number | null;
  SlaResolutionPercent: number | null;
  TicketsOpened: number | null;
  TicketsClosed: number | null;
  /** Closed ÷ opened, computed in SQL. Null when nothing was opened. */
  KillRatio: number | null;
  Notes: string | null;
}

export interface ServiceTargets {
  CsatTarget: number;
  BillableTarget: number;
  SlaResponseTarget: number;
  SlaAcknowledgeTarget: number;
  SlaResolutionTarget: number;
  KillRatioTarget: number;
}

export interface ServiceResource {
  ResourceId: number;
  Code: string;
  Name: string;
  Description: string | null;
  Url: string | null;
  Icon: string | null;
  SortOrder: number;
}

export interface ServiceDelivery {
  /** Newest first — the page reads periods[0] as "this month". */
  periods: ServicePeriod[];
  targets: ServiceTargets | null;
  resources: ServiceResource[];
}

/* --- Daily Disciplines ----------------------------------------------------
   The cadence behind the six gauges: what the service manager does daily,
   weekly and monthly, and whether it was done in the occurrence it is
   currently in. See migration 051. */

export type Cadence = 'daily' | 'weekly' | 'monthly';

export interface ServiceDiscipline {
  DisciplineId: number;
  Cadence: Cadence;
  /** Set on the standard cadence seeded in db/seed/009; null on anything
   *  somebody added here. */
  Code: string | null;
  Title: string;
  Detail: string | null;
  OwnerRoleId: number | null;
  /** The owning role's title, joined for display. */
  OwnerRole: string | null;
  SortOrder: number;
  /** The day, Monday or first of the month this row's tick counts for. */
  PeriodStart: string;
  /** Null until it has been performed in the current occurrence — which is
   *  how the box empties itself at the turn of the day, week or month. */
  DoneUtc: string | null;
  DoneBy: string | null;
  /** The last occurrence before this one it was kept in, if any. */
  LastDoneOn: string | null;
}

export interface ServiceDisciplines {
  /** The current occurrence of each cadence, from the server: the boundary is
   *  a business date, so the browser must not work it out for itself. */
  periods: Record<Cadence, string> | null;
  disciplines: ServiceDiscipline[];
}

/* --- CSAT -----------------------------------------------------------------
   The customer satisfaction survey. These shapes are camelCase, unlike the
   rest of this file: they do not come straight off SQL columns — the API
   aggregates rows into them — so there is no column name to preserve. */

export type RatingKey = 'green' | 'yellow' | 'red';

export interface CsatResponseRow {
  id: string;
  ticketId: string;
  rating: RatingKey;
  ratingValue: number;
  ratingLabel: string;
  notes: string;
  client?: string;
  customer?: string;
  agent?: string;
  subject?: string;
  /** How it reached us, and therefore how much of the above is proven rather
   *  than merged in by the PSA. See migration 079. */
  linkKind: 'ticket' | 'template';
  submittedAt: string;
}

interface CsatCounts {
  total: number;
  green: number;
  yellow: number;
  red: number;
}

export interface CsatTrendBucket extends CsatCounts {
  label: string;
  start: string;
}

export interface CsatClientSummary extends CsatCounts {
  client: string;
  /** Null rather than 0 when there is nothing to divide — "no data" must not
   *  read as "terrible". */
  csat: number | null;
  lastAt: string;
}

export interface CsatStats {
  /** Null when the window came from explicit dates, or when it is all time. */
  rangeDays: number | null;
  from: string | null;
  /** Exclusive. */
  to: string | null;
  generatedAt: string;
  totals: CsatCounts & { csat: number | null; withNotes: number; clients: number };
  trend: CsatTrendBucket[];
  clients: CsatClientSummary[];
  recent: CsatResponseRow[];
  /** The scan cap was hit, so these totals are a partial view. */
  truncated: boolean;
}

export interface CsatSnippet {
  ticketId: string;
  mode: 'ticket' | 'template';
  html: string;
  links: Record<RatingKey, string>;
}

/** What the public feedback page is told about the ticket it was opened for. */
export interface CsatContext {
  /** Null on a template link whose PSA merged nothing into it. */
  ticketId: string | null;
  rating: RatingKey;
  ratingLabel: string;
  agent: string | null;
  subject: string | null;
}

/* --- Compliance ----------------------------------------------------------- */

/** The distinction that changes what you do about it: regulatory and
 *  contractual are enforceable against you by somebody, the rest are not. */
export type ComplianceCategory =
  | 'regulatory'
  | 'contractual'
  | 'voluntary'
  | 'insurance'
  | 'internal';

export type Applicability =
  | 'unreviewed'
  | 'required'
  | 'recommended'
  | 'considering'
  | 'not_applicable';

export type ComplianceStatus = 'not_started' | 'assessing' | 'remediating' | 'maintained';

export interface ComplianceFamily {
  FamilyId: number;
  FrameworkId: number;
  Code: string;
  Title: string;
  Description: string | null;
  RequirementCount: number;
  SortOrder: number;
}

/** A requirement as a name only — for reading a family's shape, not scoring it.
 *  The assessment workspace carries the full record. */
export interface ComplianceRequirementName {
  RequirementId: number;
  Code: string;
  Title: string;
  /** Comma-delimited scope levels, or 'all'. */
  Applies: string;
  SortOrder: number;
}

export interface ComplianceFramework {
  FrameworkId: number;
  Code: string;
  Name: string;
  ShortName: string | null;
  Category: ComplianceCategory;
  Summary: string | null;
  AppliesTo: string | null;
  Authority: string | null;
  AssessmentType: 'self' | 'third_party' | 'self_or_third_party';
  ScopeLevels: string | null;
  ReferenceUrl: string | null;
  FamilyCount: number;
  RequirementCount: number;
  SortOrder: number;
  /* --- what we decided about it --- */
  Applicability: Applicability;
  Status: ComplianceStatus;
  OwnerEmployeeId: number | null;
  OwnerName: string | null;
  LastAssessedOn: string | null;
  NextReviewDate: string | null;
  EvidenceUrl: string | null;
  Notes: string | null;
  IsInScope: boolean;
  ReviewOverdue: boolean;
  families: ComplianceFamily[];
  /** Scope levels available when starting an assessment. */
  levels: { FrameworkId: number; LevelKey: string; Label: string }[];
}

/* --- Compliance assessment ------------------------------------------------ */

export type ResultStatus = 'not_assessed' | 'met' | 'partial' | 'not_met' | 'not_applicable';

export interface ComplianceRequirement {
  RequirementId: number;
  FamilyId: number;
  Code: string;
  Title: string;
  Description: string | null;
  /** Comma-delimited scope levels: 'ig1,ig2,ig3' or 'all'. */
  Applies: string;
  /** Framework-specific tags plus the "what goes wrong without it" narrative. */
  Meta: string | null;
  SortOrder: number;
  Status: ResultStatus;
  Notes: string | null;
  AssessedOn: string | null;
  AssessedByName: string | null;
  EvidenceCount: number;
  /* The risk recorded on it (migration 157), for frameworks that score risk. */
  Likelihood: number | null;
  Impact: number | null;
  Treatment: 'mitigate' | 'accept' | 'transfer' | 'avoid' | null;
  Finding: string | null;
  Remediation: string | null;
  RemediationOwner: string | null;
  TargetDate: string | null;
}

export interface ComplianceEvidence {
  EvidenceId: number;
  RequirementId: number;
  Label: string;
  Url: string | null;
  EvidenceType: 'policy' | 'screenshot' | 'config' | 'report' | 'ticket' | 'attestation' | 'other';
  Notes: string | null;
  CreatedUtc: string;
  AddedByName: string | null;
}

/**
 * The subject of an assessment: a company from `core.Client`, or us when null.
 *
 * The canonical company list, not the sales pipeline — see migration 055. Only
 * live companies are offered; the deactivated ones are off every picker.
 */
export interface ComplianceSubject {
  ClientId: number;
  Name: string;
  Status: 'active' | 'prospect' | 'former' | 'partner' | 'inactive';
  Industry: string | null;
  /** The AM module looks after them. Worth saying in the picker. */
  IsManaged: boolean;
  /** How many frameworks are already being assessed for them. */
  Assessments: number;
}

export interface AssessmentSummaryRow {
  AssessmentId: number;
  FrameworkId: number;
  /** Null is our own organisation, which is a real subject rather than "none". */
  ClientId: number | null;
  ClientName: string | null;
  FrameworkCode: string;
  FrameworkName: string;
  FrameworkShortName: string | null;
  Category: ComplianceCategory;
  ScopeLevelKey: string;
  ScopeLevelLabel: string | null;
  Status: 'in_progress' | 'complete' | 'archived';
  OwnerEmployeeId: number | null;
  OwnerName: string | null;
  StartedOn: string | null;
  CompletedOn: string | null;
  Notes: string | null;
  InScope: number;
  Met: number;
  Partial: number;
  NotMet: number;
  NotApplicable: number;
  NotAssessed: number;
  /** In scope minus not-applicable — the denominator of the score. */
  Applicable: number;
  /** Null when nothing is applicable, which is not the same as zero. */
  ScorePct: number | null;
}

/**
 * One assessment in full.
 *
 * Deliberately carries no tallies: it ships every in-scope requirement with
 * its status, so the page counts them itself and the score moves the instant
 * a button is pressed. Asking the server to re-count after every click would
 * put a round trip between the click and the number.
 */
export interface ComplianceAssessment {
  AssessmentId: number;
  FrameworkId: number;
  ClientId: number | null;
  ClientName: string | null;
  FrameworkCode: string;
  FrameworkName: string;
  FrameworkShortName: string | null;
  Category: ComplianceCategory;
  Summary: string | null;
  Authority: string | null;
  /** The framework's method as JSON — steps, statuses, risk scales (migration 157). */
  Guidance: string | null;
  ScopeLevelKey: string;
  ScopeLevelLabel: string | null;
  Status: 'in_progress' | 'complete' | 'archived';
  OwnerEmployeeId: number | null;
  OwnerName: string | null;
  StartedOn: string | null;
  CompletedOn: string | null;
  Notes: string | null;
  levels: { LevelKey: string; Label: string }[];
  families: {
    FamilyId: number;
    Code: string;
    Title: string;
    Description: string | null;
    SortOrder: number;
  }[];
  requirements: ComplianceRequirement[];
  evidence: ComplianceEvidence[];
}

export interface ComplianceSummary {
  Frameworks: number;
  /** Organisations with at least one decision recorded — the denominator
   *  that makes the subject x framework pair counts readable. */
  Subjects: number;
  InScope: number;
  Required: number;
  Maintained: number;
  /** In scope with nobody's name against it. */
  Unowned: number;
  ReviewsOverdue: number;
  ReviewsDueSoon: number;
  /** Never ruled in or out. Zero means somebody has been through the catalogue. */
  Unreviewed: number;
  RequirementsInScope: number;
}

/* --- Sales CRM ------------------------------------------------------------ */

export type AccountType = 'prospect' | 'client' | 'former' | 'partner';

export type LeadSource =
  | 'referral'
  | 'outbound'
  | 'marketing'
  | 'partner'
  | 'event'
  | 'existing_client'
  | 'other';

export type ServiceType =
  | 'managed_services'
  | 'co_managed'
  | 'security'
  | 'cloud'
  | 'backup'
  | 'voice'
  | 'project'
  | 'hardware'
  | 'other';

export type ActivityType =
  | 'call'
  | 'email'
  | 'meeting'
  | 'assessment'
  | 'proposal'
  | 'qbr'
  | 'task'
  | 'note';

export interface SalesStage {
  StageId: number;
  Code: string;
  Name: string;
  Description: string | null;
  Probability: number;
  SortOrder: number;
  IsClosed: boolean;
  IsWon: boolean;
}

/** An archived deal, as /sales/opportunities/archived returns it. */
export interface ArchivedDeal {
  OpportunityId: number;
  AccountId: number;
  AccountName: string | null;
  Name: string;
  StageName: string;
  IsClosed: boolean;
  IsWon: boolean;
  OwnerName: string | null;
  Mrr: number;
  OneTimeValue: number;
  AnnualValue: number;
  ExpectedCloseDate: string | null;
  ClosedOn: string | null;
  LostReason: string | null;
  Notes: string | null;
  ArchivedUtc: string;
  ArchivedBy: string | null;
}

export interface SalesOpportunity {
  OpportunityId: number;
  AccountId: number;
  AccountName: string;
  AccountType?: AccountType;
  Name: string;
  StageId: number;
  StageName: string;
  IsClosed: boolean;
  IsWon: boolean;
  OwnerEmployeeId: number | null;
  OwnerName: string | null;
  ServiceType: ServiceType;
  /** Monthly recurring revenue — the number the business is actually built on. */
  Mrr: number;
  OneTimeValue: number;
  TermMonths: number;
  Seats: number | null;
  /** Null means "use the stage probability"; `EffectiveProbability` resolves it. */
  Probability: number | null;
  EffectiveProbability: number;
  /** MRR over twelve months plus the one-off. The headline deal value. */
  AnnualValue: number;
  TotalContractValue: number;
  WeightedValue?: number;
  ExpectedCloseDate: string | null;
  NextStep: string | null;
  NextStepDate: string | null;
  Source: LeadSource | null;
  Competitor: string | null;
  ClosedOn: string | null;
  LostReason: string | null;
  Notes: string | null;
  SortOrder: number;
  IsOverdue?: boolean;
  NextStepOverdue?: boolean;
  LastActivityOn?: string | null;
}

export interface PipelineStage extends SalesStage {
  deals: SalesOpportunity[];
}

export interface SalesContact {
  ContactId: number;
  AccountId: number;
  FirstName: string;
  LastName: string;
  Title: string | null;
  Email: string | null;
  Phone: string | null;
  IsPrimary: boolean;
  DecisionRole:
    | 'economic'
    | 'technical'
    | 'champion'
    | 'influencer'
    | 'user'
    | 'blocker'
    | 'unknown';
  Notes: string | null;
}

export interface SalesAccount {
  AccountId: number;
  Name: string;
  AccountType: AccountType;
  Industry: string | null;
  Website: string | null;
  /* The postal address is the company's and lives on core.Client — see
     migration 056. It is served through the account because this is the form
     that collects it. */
  Address: string | null;
  City: string | null;
  Region: string | null;
  PostalCode: string | null;
  /** Supported users. The MSP unit of size, and what per-seat pricing keys off. */
  Seats: number | null;
  CurrentMrr: number | null;
  OwnerEmployeeId: number | null;
  OwnerName: string | null;
  Source: LeadSource | null;
  Status: 'active' | 'inactive';
  Notes: string | null;
  ContactCount?: number;
  OpenDeals?: number;
  PipelineValue?: number;
  LastActivityOn?: string | null;
  /** The primary contact, in full — the next form after an account usually
   *  asks for this person. */
  PrimaryContactName?: string | null;
  PrimaryContactId?: number | null;
  PrimaryContactFirstName?: string | null;
  PrimaryContactLastName?: string | null;
  PrimaryContactTitle?: string | null;
  PrimaryContactEmail?: string | null;
  PrimaryContactPhone?: string | null;
}

export interface SalesAccountDetail extends SalesAccount {
  contacts: SalesContact[];
  opportunities: SalesOpportunity[];
  activities: SalesActivity[];
}

export interface SalesActivity {
  ActivityId: number;
  AccountId: number;
  AccountName?: string;
  OpportunityId: number | null;
  OpportunityName: string | null;
  ActivityType: ActivityType;
  Subject: string;
  Detail: string | null;
  DueDate: string | null;
  CompletedOn: string | null;
  OwnerEmployeeId: number | null;
  OwnerName: string | null;
  IsOverdue?: boolean;
}

export interface SalesSummary {
  /** The window archived deals were counted in — this quarter unless the page chose another — and how many were. */
  ArchivedFrom: string;
  ArchivedTo: string;
  ArchivedCounted: number;
  OpenDeals: number;
  OpenPipelineValue: number;
  OpenPipelineMrr: number;
  WeightedForecast: number;
  WonThisQuarter: number;
  WonMrrThisQuarter: number;
  WonValueThisQuarter: number;
  LostThisQuarter: number;
  /** Trailing twelve months. Null when nothing has closed — not the same as 0. */
  WinRate: number | null;
  OverdueCloseDates: number;
  DealsWithoutNextStep: number;
  OverdueActivities: number;
  Prospects: number;
  Clients: number;
  ClientMrr: number;
}

/* --- Onboarding ----------------------------------------------------------
   The standalone onboarding app, moved in under Sales. See migration 054. */

export type OnboardingCategory =
  | 'communication'
  | 'setup'
  | 'security'
  | 'documentation'
  | 'onboarding'
  | 'internal'
  | 'other';

export type OnboardingTaskStatus = 'pending' | 'in_progress' | 'complete';

export type OnboardingStatus = 'in_progress' | 'handed_over' | 'cancelled';

/** A checkbox on the wizard. Services, operating systems and cloud platforms
 *  are one catalogue with a `Kind`, because the trigger rules key off both. */
export interface OnboardingOption {
  OptionId: number;
  Kind: 'service' | 'platform' | 'os';
  Code: string;
  Name: string;
  Description: string | null;
  SortOrder: number;
}

export interface OnboardingTemplate {
  TemplateId: number;
  Title: string;
  Description: string | null;
  Category: OnboardingCategory;
  DefaultDueDays: number;
  RequiresUpload: boolean;
  UploadLabel: string | null;
  EmailSubject: string | null;
  EmailBody: string | null;
  InternalNotes: string | null;
  SortOrder: number;
  /** What pulls this entry in. `ByCategory` means the rule swept the whole
   *  category rather than naming this task. */
  Triggers: { Kind: string; Value: string; ByCategory: boolean }[];
  /** Retiring only sets this flag — a retired entry keeps its trigger rules
   *  and the tasks already assigned from it, which is what makes it restorable. */
  IsActive: boolean;
  /** What this task's email sends — see migration 182. */
  files: OnboardingTemplateFile[];
}

/** A file kept on a library entry and attached to its task's email. */
export interface OnboardingTemplateFile {
  FileId: number;
  TemplateId: number;
  FileName: string;
  MimeType: string;
  SizeBytes: number;
  UploadedUtc: string;
}

export interface OnboardingFile {
  FileId: number;
  TaskId: number;
  FileName: string;
  MimeType: string;
  SizeBytes: number;
  UploadedUtc: string;
  UploadedBy: string | null;
}

export interface OnboardingTask {
  TaskId: number;
  OnboardingId: number;
  TemplateId: number | null;
  /** A trigger rule put this here. Only these are ever withdrawn when the
   *  answers change — see routes/onboarding.ts. */
  IsAutoAssigned: boolean;
  Title: string;
  Description: string | null;
  Category: OnboardingCategory;
  RequiresUpload: boolean;
  UploadLabel: string | null;
  EmailSubject: string | null;
  EmailBody: string | null;
  InternalNotes: string | null;
  Status: OnboardingTaskStatus;
  DueDate: string | null;
  OwnerEmployeeId: number | null;
  OwnerName: string | null;
  CompletedUtc: string | null;
  SortOrder: number;
  files: OnboardingFile[];
  /** The library entry's attachments, read through rather than copied, so
   *  replacing the form in the library changes what the next email sends. */
  emailFiles: OnboardingTemplateFile[];
}

/** A row on the onboarding list. */
export interface OnboardingSummaryRow {
  OnboardingId: number;
  AccountId: number;
  OpportunityId: number | null;
  Status: OnboardingStatus;
  ContactFirstName: string | null;
  ContactLastName: string | null;
  ContactEmail: string | null;
  UserCount: number | null;
  Timeline: string | null;
  SubmittedUtc: string;
  HandedOverUtc: string | null;
  AmClientId: string | null;
  AccountName: string | null;
  Industry: string | null;
  AccountType: AccountType;
  OpportunityName: string | null;
  Mrr: number | null;
  HandedOverBy: string | null;
  TaskCount: number;
  TasksComplete: number;
  TasksOverdue: number;
  /** Pipe-separated service codes — STRING_AGG, so one round trip for the list. */
  Services: string | null;
}

export interface OnboardingDetail {
  OnboardingId: number;
  AccountId: number;
  ClientId: number | null;
  OpportunityId: number | null;
  Status: OnboardingStatus;

  AccountName: string | null;
  Industry: string | null;
  Website: string | null;
  AccountType: AccountType;
  Seats: number | null;
  CurrentMrr: number | null;
  OpportunityName: string | null;
  OpportunityMrr: number | null;
  OneTimeValue: number | null;

  CompanySize: string | null;
  Address: string | null;
  City: string | null;
  Region: string | null;
  PostalCode: string | null;

  ContactFirstName: string | null;
  ContactLastName: string | null;
  ContactTitle: string | null;
  ContactEmail: string | null;
  ContactPhone: string | null;
  PreferredContact: 'email' | 'phone' | 'either';

  BackupType: 'cloud' | 'server' | 'both' | null;
  ServiceNotes: string | null;

  UserCount: number | null;
  CurrentProvider: string | null;
  PainPoints: string | null;
  Timeline: string | null;

  AmClientId: string | null;
  HandedOverUtc: string | null;
  HandedOverBy: string | null;
  SubmittedUtc: string;

  services: string[];
  platforms: string[];
  operatingSystems: string[];
  tasks: OnboardingTask[];
}

export interface OnboardingSummary {
  InFlight: number;
  OverdueTasks: number;
  OpenTasks: number;
  /** Everything ticked and nobody has taken the client over yet. */
  ReadyForHandover: number;
  HandedOverLast90: number;
  AvgDaysToHandover: number | null;
}

/* --- V/TO ---------------------------------------------------------------- */

export type RockStatus = 'not_started' | 'on_track' | 'off_track' | 'complete';

/** The unit an item resolved to, and how — see api/src/lib/unitAttribution.ts. */
export interface UnitAttributed {
  ResolvedUnitId: number | null;
  AttributedBy: AttributedBy;
  /** The EOS meeting it was raised in, when it has one. */
  MeetingName: string | null;
}

export interface Rock extends UnitAttributed {
  RockId: number;
  Title: string;
  Description: string | null;
  OwnerEmployeeId: number | null;
  OwnerName: string | null;
  Quarter: string | null;
  DueDate: string | null;
  Status: RockStatus;
  IsCompanyRock: boolean;
  SortOrder: number;
}

export interface Issue extends UnitAttributed {
  IssueId: number;
  Title: string;
  Description: string | null;
  OwnerEmployeeId: number | null;
  OwnerName: string | null;
  Term: 'long' | 'short';
  Priority: 'high' | 'medium' | 'low';
  Status: 'open' | 'in_progress' | 'resolved' | 'dropped';
  Resolution: string | null;
  SortOrder: number;
}

export interface PictureItem {
  PictureItemId: number;
  PictureId: number;
  Text: string;
  IsComplete: boolean;
  SortOrder: number;
}

export interface Picture {
  PictureId: number;
  Horizon: 'three_year' | 'one_year';
  FutureDate: string | null;
  Revenue: number | null;
  Profit: number | null;
  Measurables: string | null;
  items: PictureItem[];
}

export interface Vto {
  VtoId: number;
  Name: string;
  IsCurrent: boolean;
  Purpose: string | null;
  Niche: string | null;
  TenYearTarget: string | null;
  TenYearDate: string | null;
  TargetMarket: string | null;
  ProvenProcess: string | null;
  Guarantee: string | null;
  coreValues: { CoreValueId: number; Value: string; Description: string | null; SortOrder: number }[];
  uniques: { UniqueId: number; Text: string; SortOrder: number }[];
  pictures: Picture[];
  rocks: Rock[];
  issues: Issue[];
  /** Every active unit, in display order — including the ones with nothing in
   *  them, so an empty column is knowably empty rather than merely absent. */
  units: { BusinessUnitId: number; Name: string }[];
}

/* --- OML assessment ------------------------------------------------------ */

export interface Criterion {
  CriterionId: number;
  Text: string;
  LevelDescriptors: string | null;
  SortOrder: number;
  score: number | null;
  notes: string | null;
}

export interface Dimension {
  DimensionId: number;
  Name: string;
  Description: string | null;
  Weight: number;
  SortOrder: number;
  /** The department this section scores. Many sections can share one. */
  DepartmentId: number | null;
  DepartmentName: string | null;
  criteria: Criterion[];
  average: number | null;
  scoredCount: number;
  criteriaCount: number;
}

/* --- The question set, as the admin editor sees it ------------------------
   A separate shape from `Dimension`/`Criterion` above, which carry an
   assessment's ANSWERS. These carry what it would cost to change the question.
   ------------------------------------------------------------------------- */

export interface EditableCriterion {
  CriterionId: number;
  DimensionId: number;
  Text: string;
  /** JSON keyed "1".."5", or null for a question with no written rungs. */
  LevelDescriptors: string | null;
  SortOrder: number;
  /** The id this question had in the system it was imported from, or null. */
  SourceKey: string | null;
  /** Set once somebody has edited it here, which stops the seed re-writing it. */
  EditedUtc: string | null;
  /** Scores already recorded against it — what a reword re-labels. */
  AnswerCount: number;
  AssessmentCount: number;
}

export interface EditableDimension {
  DimensionId: number;
  /** The section heading, which is the dimension's own and nothing else's. */
  Name: string;
  Description: string | null;
  Weight: number;
  SortOrder: number;
  /** The department the section scores — editable here, unlike everything
   *  else about the instrument. Null is a real answer: a section about
   *  something no one department owns. */
  DepartmentId: number | null;
  DepartmentName: string | null;
  /** The business area its improvement plan reads the process library from.
   *  Its own, not the department's: Engineering covers four areas. */
  AreaId: number | null;
  AreaName: string | null;
  EditedUtc: string | null;
  criteria: EditableCriterion[];
}

export interface QuestionSet {
  TemplateId: number;
  Code: string;
  Version: number;
  Name: string;
  Description: string | null;
  ScaleMin: number;
  ScaleMax: number;
  /** The two lists a section's links are chosen from, in the same payload. */
  departments: { DepartmentId: number; Name: string }[];
  areas: { AreaId: number; Code: string; Name: string }[];
  dimensions: EditableDimension[];
}

export interface Assessment {
  AssessmentId: number;
  Title: string;
  /** Which company was assessed (migration 178); null when none was given. */
  Company: string | null;
  /** Brought across by the OML sync, which puts its name, company and date back on each run. */
  Synced: boolean;
  PeriodLabel: string | null;
  ConductedOn: string | null;
  Status: 'draft' | 'in_progress' | 'complete';
  Summary: string | null;
  dimensions: Dimension[];
  overallScore: number | null;
}

/* --- Maturity playbook ---------------------------------------------------- */

export interface PlaybookItem {
  PlaybookId: number;
  AreaCode: string;
  TargetLevel: number;
  Kind: 'process' | 'kpi';
  Title: string;
  Detail: string | null;
  /** KPIs only — the number to hit at this level. */
  MetricTarget: string | null;
  Source: string | null;
  SortOrder: number;
  /** Set once the suggestion has been pushed onto the V/TO issues list. */
  IssueId: number | null;
  PromotedUtc: string | null;
}

/**
 * A process the improvement plan proposes, from either catalogue.
 *
 * `Origin` is the discriminator and decides which endpoint promotes it — a
 * library process and a playbook suggestion carry ids from different tables.
 * The library is preferred and the seeded playbook is the fallback; see the
 * playbook handler for why.
 */
export type PlaybookProcess =
  | {
      Origin: 'library';
      /** `coo.Process` — the row the Processes page maintains. */
      ProcessId: number;
      AreaCode: string;
      /** The process's own OML level, which is the level being climbed to. */
      TargetLevel: number;
      Title: string;
      Detail: string | null;
      OwnerName: string | null;
      IssueId: number | null;
      PromotedUtc: string | null;
    }
  | (PlaybookItem & { Origin: 'playbook'; OwnerName: null });

export interface PlaybookDimension {
  DimensionId: number;
  Name: string;
  DepartmentName: string | null;
  SortOrder: number;
  Average: number | null;
  ScoredCount: number;
  CriteriaCount: number;
  AreaCode: string | null;
  CurrentLevel: number;
  /** Null when the department is already at level 5 — nowhere left to climb. */
  TargetLevel: number | null;
  processes: PlaybookProcess[];
  kpis: PlaybookItem[];
}

export interface AssessmentSummary {
  AssessmentId: number;
  Title: string;
  Company: string | null;
  /** Brought across by the OML sync, which puts its name, company and date back on each run. */
  Synced: boolean;
  PeriodLabel: string | null;
  ConductedOn: string | null;
  Status: 'draft' | 'in_progress' | 'complete';
  TemplateName: string;
  ScoredCount: number;
  CriteriaCount: number;
}

/* --- Business review ----------------------------------------------------- */

export interface ReviewMetric {
  MetricId: number;
  Name: string;
  Category: string | null;
  OwnerEmployeeId: number | null;
  OwnerName: string | null;
  TargetValue: number | null;
  ActualValue: number | null;
  Unit: 'number' | 'currency' | 'percent' | 'hours' | 'days' | 'ratio';
  Direction: 'higher' | 'lower';
  Commentary: string | null;
  SortOrder: number;
}

export interface ReviewAction {
  ActionId: number;
  Description: string;
  OwnerEmployeeId: number | null;
  OwnerName: string | null;
  DueDate: string | null;
  Status: 'open' | 'in_progress' | 'complete' | 'dropped';
  SortOrder: number;
}

export interface ReviewSection {
  SectionId: number;
  Title: string;
  Body: string | null;
  SortOrder: number;
}

/** One row of coo.FeatureRequest — migration 168. */
export interface FeatureRequest {
  FeatureRequestId: number;
  Name: string;
  Request: string;
  PageUrl: string;
  PageLabel: string | null;
  SubmittedByName: string | null;
  SubmittedByEmail: string | null;
  CreatedUtc: string;
  /* Triage — migration 169. Priority is empty until somebody decides it. */
  Priority: FeatureRequestPriority | null;
  Status: FeatureRequestStatus;
  /** What was done about it — migration 189. */
  WorkNote: string | null;
  /** Its screenshots — migration 191. Fetched one by one when it is opened. */
  ImageIds: number[];
  UpdatedBy: string | null;
  UpdatedUtc: string | null;
}

export type FeatureRequestPriority = 'low' | 'medium' | 'high';
export type FeatureRequestStatus = 'new' | 'in-progress' | 'testing' | 'completed' | 'not-implemented';

export interface BusinessReview {
  ReviewId: number;
  Title: string;
  PeriodType: 'monthly' | 'bi-monthly' | 'quarterly' | 'bi-annual' | 'annual';
  PeriodLabel: string | null;
  PeriodStart: string | null;
  PeriodEnd: string | null;
  Status: 'draft' | 'published';
  ExecutiveSummary: string | null;
  metrics: ReviewMetric[];
  sections: ReviewSection[];
  actions: ReviewAction[];
}

export interface ReviewSummary {
  ReviewId: number;
  Title: string;
  PeriodType: string;
  PeriodLabel: string | null;
  PeriodStart: string | null;
  PeriodEnd: string | null;
  Status: 'draft' | 'published';
  MetricCount: number;
  OpenActionCount: number;
}

/* --- Charter -------------------------------------------------------------- */

export interface Charter {
  CharterId: number;
  Title: string;
  Version: string;
  EffectiveDate: string | null;
  ReviewDate: string | null;
  HolderEmployeeId: number | null;
  HolderName: string | null;
  Status: 'draft' | 'active' | 'superseded';
  Mission: string | null;
  sections: { SectionId: number; SectionKey: string | null; Title: string; Body: string | null; SortOrder: number }[];
  metrics: { CharterMetricId: number; Name: string; Target: string | null; Cadence: string; SortOrder: number }[];
}

/* --- Ownership dependency ------------------------------------------------ */

export type TransitionStatus = 'not_started' | 'documenting' | 'training' | 'delegated' | 'complete';

export interface DependencyItem {
  ItemId: number;
  AreaId: number;
  Activity: string;
  Description: string | null;
  CurrentOwnerEmployeeId: number | null;
  CurrentOwnerName: string | null;
  TargetOwnerEmployeeId: number | null;
  TargetOwnerName: string | null;
  DependencyLevel: number;
  BusinessImpact: number;
  RiskScore: number;
  IsDocumented: boolean;
  DocumentationUrl: string | null;
  TransitionStatus: TransitionStatus;
  TargetDate: string | null;
  Notes: string | null;
  SortOrder: number;
}

export interface DependencyArea {
  AreaId: number;
  Name: string;
  Description: string | null;
  SortOrder: number;
  items: DependencyItem[];
}

/* --- Accountability chart ------------------------------------------------ */

export type GwcVerdict = 'yes' | 'unsure' | 'no';

export interface SeatAssignment {
  AssignmentId: number;
  SeatId: number;
  EmployeeId: number;
  EmployeeName: string;
  Email: string | null;
  DepartmentName: string | null;
  RoleTitle: string | null;
  IsPrimary: boolean;
  GwcVerdict: GwcVerdict | null;
}

export interface Seat {
  SeatId: number;
  ParentSeatId: number | null;
  Name: string;
  RoleId: number | null;
  RoleTitle: string | null;
  DepartmentId: number | null;
  DepartmentName: string | null;
  SortOrder: number;
  responsibilities: { ResponsibilityId: number; Text: string; SortOrder: number }[];
  assignments: SeatAssignment[];
  children: Seat[];
}

export interface AccountabilityChart {
  ChartId: number;
  Name: string;
  EffectiveDate: string | null;
  IsCurrent: boolean;
  seats: Seat[];
}

/* --- GWC ------------------------------------------------------------------ */

export interface GwcRow {
  EmployeeId: number;
  FullName: string;
  Email: string | null;
  DepartmentName: string | null;
  RoleTitle: string | null;
  IsLeadership: boolean | null;
  GwcId: number | null;
  SeatId: number | null;
  SeatName: string | null;
  AssessedOn: string | null;
  GetIt: GwcVerdict | null;
  GetItScore: number | null;
  GetItNotes: string | null;
  WantIt: GwcVerdict | null;
  WantItScore: number | null;
  WantItNotes: string | null;
  Capacity: GwcVerdict | null;
  CapacityScore: number | null;
  CapacityNotes: string | null;
  Verdict: GwcVerdict | null;
  ActionPlan: string | null;
  NextReviewDate: string | null;
}

/* --- People (the HR Toolbox, moved in) ------------------------------------
   There is no employee type here. People in this module ARE `Employee` rows
   from `core` — see migration 032. What follows is the HR half that hangs off
   them, plus the module's own records. */

export type ResponseType = 'rating' | 'text' | 'yesno';

export interface PeopleSummary {
  People: number;
  Templates: number;
  TrainingRoles: number;
  TrainingItems: number;
  ReviewsTotal: number;
  ReviewsOpen: number;
  ReviewsDue: number;
  JobDescriptions: number;
  Scorecards: number;
  /** Required training owed and done, across everyone with a template. */
  RequiredTraining: number;
  RequiredTrainingDone: number;
  openReviews: {
    ReviewId: number;
    ReviewDate: string | null;
    TemplateName: string | null;
    EmployeeId: number;
    EmployeeName: string;
    ReviewerName: string | null;
  }[];
  reviewsDue: {
    EmployeeId: number;
    FullName: string;
    RoleTitle: string | null;
    LastReviewDate: string | null;
  }[];
}

/** A row on the People list: the directory facts, plus this module's own. */
export interface PersonRow {
  EmployeeId: number;
  FullName: string;
  Email: string | null;
  Status: Employee['Status'];
  DepartmentName: string | null;
  RoleTitle: string | null;
  StartDate: string | null;
  TemplateId: number | null;
  TemplateName: string | null;
  LastReviewDate: string | null;
  PreferredName: string | null;
  ReviewCount: number;
  OpenReviewCount: number;
  RequiredTraining: number;
  RequiredTrainingDone: number;
}

/** One person, opened. The directory columns are read-only here — they are
 *  `core.Employee`, and only the Employees page writes to `core`. */
export interface PersonDetail {
  EmployeeId: number;
  FirstName: string;
  LastName: string;
  FullName: string;
  Email: string | null;
  Status: Employee['Status'];
  EmploymentType: Employee['EmploymentType'];
  StartDate: string | null;
  EndDate: string | null;
  DepartmentId: number | null;
  DepartmentName: string | null;
  RoleId: number | null;
  RoleTitle: string | null;
  ManagerEmployeeId: number | null;
  ManagerName: string | null;
  /** Whether the person reading may open the Activity tab: an admin, or this
   *  person's manager anywhere up the chart. */
  CanSeeActivity: boolean;
  /** Derived, not stored on the person: the template belonging to the job role
   *  in `RoleId`. Null when that role has no training row. Read-only — it is
   *  changed by changing the role. */
  TemplateId: number | null;
  LastReviewDate: string | null;

  PreferredName: string | null;
  Location: string | null;
  Hometown: string | null;
  SpousePartner: string | null;
  Kids: string | null;
  Pets: string | null;
  LivingSituation: string | null;
  RecentLifeEvent: string | null;
  UpcomingLifeEvent: string | null;
  Hobbies: string | null;
  SportsTeams: string | null;
  VacationType: string | null;
  DiscProfile: string | null;
  CommunicationStyle: string | null;
  EnergizedBy: string | null;
  DrainedBy: string | null;
  Goal1Yr: string | null;
  Goal3To5Yr: string | null;
  SkillsToDevelop: string | null;
  MostProudOf: string | null;
  PrimaryMotivator: string | null;
  RecognitionPreference: string | null;
  BiggestFrustration: string | null;
  HowWeMet: string | null;
  TrustLevel: string | null;
  SensitiveNotes: string | null;
  OneOnOneStyle: string | null;
  LastMeaningfulConvo: string | null;
  Notes: string | null;
  /** Date of birth, for the Admin module's birthday board. Frequently null, and
   *  that stays true — see migration 048. */
  BirthDate: string | null;
}

/** The profile fields this module owns and may write — everything on a person
 *  that is NOT a `core.Employee` column. */
export type ProfileField = Exclude<
  keyof PersonDetail,
  | 'EmployeeId' | 'FirstName' | 'LastName' | 'FullName' | 'Email' | 'Status'
  | 'EmploymentType' | 'StartDate' | 'EndDate'
  | 'DepartmentId' | 'DepartmentName' | 'RoleId' | 'RoleTitle'
  | 'ManagerEmployeeId' | 'ManagerName' | 'TemplateId' | 'LastReviewDate'
>;

export interface TemplateQuestion {
  QuestionId: number;
  SectionId: number;
  Text: string;
  ResponseType: ResponseType;
  SortOrder: number;
}

export interface TemplateSection {
  SectionId: number;
  Title: string;
  SortOrder: number;
  questions: TemplateQuestion[];
}

export interface PeopleTemplate {
  TemplateId: number;
  Name: string;
  /** The job role this template is for, chosen from the directory. */
  RoleId: number | null;
  /** Derived: `core.Role.Title` for `RoleId`. Read-only — set RoleId instead. */
  RoleName: string | null;
  Description: string | null;
  JobDescription: string | null;
  /** Sensitive. The People card is granted per person partly because of this. */
  PayScale: string | null;
  CreatedUtc: string;
  sections: TemplateSection[];
}

export interface ReviewSummaryRow {
  ReviewId: number;
  TemplateId: number | null;
  TemplateName: string | null;
  ReviewDate: string | null;
  Status: 'draft' | 'complete';
  Notes: string | null;
  CreatedUtc: string;
  /** When it was shared with the person; null while it is private (migration 172). */
  SharedUtc: string | null;
  ReviewerEmployeeId: number | null;
  ReviewerName: string | null;
  QuestionCount: number;
  AnsweredCount: number;
}

export interface ReviewAnswer {
  AnswerId: number;
  QuestionId: number | null;
  QuestionText: string;
  SectionTitle: string | null;
  ResponseType: ResponseType;
  SortOrder: number;
  Rating: number | null;
  TextResponse: string | null;
  YesNoResponse: boolean | null;
}

/** The Employee Self-Assessment Form's four levels — migration 171. */
export type SelfReviewRating = 'exceeds' | 'meets' | 'needs_improvement' | 'unacceptable';

/** Which form a row is: the employee's self-assessment, or the manager's
 *  assessment of them (migration 173). Same form, different title. */
export type SelfReviewKind = 'self' | 'manager';

/** The form's wording, from GET /people/self-reviews/form. */
export interface SelfReviewForm {
  title: string;
  employeeInformation: string;
  ratedSections: { title: string; items: { key: string; category: string; description: string }[] }[];
  ratingLabels: Record<SelfReviewRating, string>;
  goals: { title: string; prompt: string };
  overall: { title: string; levels: { value: SelfReviewRating; label: string; description: string }[] };
  summaryTitle: string;
  acknowledgement: { title: string; text: string };
  /** The V/TO's core values today, rated 1-5 stars on a new review. */
  coreValues: { Name: string; Description: string | null; SortOrder: number }[];
}

/** One company core value on a review, in the words of the version it was rated against (migrations 179, 180). */
export interface SelfReviewValue {
  ValueId: number;
  Name: string;
  Description: string | null;
  SortOrder: number;
  Stars: number | null;
  Comments: string | null;
}

export interface SelfReviewItem {
  ItemId: number;
  ItemKey: string;
  SectionTitle: string;
  Category: string;
  Description: string;
  SortOrder: number;
  Rating: SelfReviewRating | null;
  Comments: string | null;
}

export interface SelfReview {
  SelfReviewId: number;
  Kind: SelfReviewKind;
  /** Manager reviews only: when it was shared; null while private. */
  SharedUtc: string | null;
  EmployeeId: number;
  EmployeeName: string;
  JobTitle: string | null;
  ReviewerEmployeeId: number | null;
  ReviewerName: string | null;
  PeriodFrom: string | null;
  PeriodTo: string | null;
  Goal1: string | null;
  Goal2: string | null;
  Goal3: string | null;
  OverallRating: SelfReviewRating | null;
  Summary: string | null;
  EmployeeSignature: string | null;
  EmployeeSignedDate: string | null;
  ReviewerSignature: string | null;
  ReviewerSignedDate: string | null;
  CreatedUtc: string;
  ModifiedUtc: string;
  items: SelfReviewItem[];
  values: SelfReviewValue[];
}

export interface SelfReviewSummaryRow {
  SelfReviewId: number;
  Kind: SelfReviewKind;
  SharedUtc: string | null;
  PeriodFrom: string | null;
  PeriodTo: string | null;
  OverallRating: SelfReviewRating | null;
  EmployeeSignature: string | null;
  EmployeeSignedDate: string | null;
  ReviewerSignature: string | null;
  ReviewerSignedDate: string | null;
  CreatedUtc: string;
  ModifiedUtc: string;
  ReviewerName: string | null;
  ItemCount: number;
  RatedCount: number;
  /** Average stars across the core values rated so far; null when none are. */
  ValuesAverage: number | null;
}

export interface PeopleReview {
  ReviewId: number;
  EmployeeId: number;
  TemplateId: number | null;
  TemplateName: string | null;
  ReviewDate: string | null;
  ReviewerEmployeeId: number | null;
  ReviewerName: string | null;
  Status: 'draft' | 'complete';
  Notes: string | null;
  CreatedUtc: string;
  SharedUtc: string | null;
  answers: ReviewAnswer[];
}

export interface TrainingRole {
  TrainingRoleId: number;
  /** The job role this list belongs to. Null only for a row left unassigned. */
  RoleId: number | null;
  /** Derived: `core.Role.Title` for `RoleId`. Null when no role is assigned. */
  Name: string | null;
  /** Set when this role mirrors a People Template rather than standing alone. */
  TemplateId: number | null;
  IsManual: boolean;
  NamePinned: boolean;
  ItemCount: number;
  RequiredCount: number;
}

export interface TrainingItem {
  TrainingItemId: number;
  TrainingRoleId: number;
  Title: string;
  Link: string | null;
  Category: 'required' | 'optional';
  SortOrder: number;
}

/** A training item with one person's completion joined on. */
export interface EmployeeTrainingItem extends Omit<TrainingItem, 'TrainingRoleId'> {
  CompletedOn: string | null;
  CompletionNotes: string | null;
}

export interface EmployeeTraining {
  role: { TrainingRoleId: number; Name: string } | null;
  items: EmployeeTrainingItem[];
  /** Approved training requests for this person — migration 187. */
  requests: ApprovedTrainingRequest[];
}

/* --- Training requests (migration 187) ----------------------------------- */

/** An approved request as the People profile shows it. */
export interface ApprovedTrainingRequest {
  TrainingRequestId: number;
  Program: string;
  Provider: string | null;
  StartDate: string | null;
  TargetDate: string | null;
  RequestedCategory: string | null;
  TotalCost: number;
  TotalHours: number;
  /** What was approved, where the supervisor approved less than was asked. */
  ApprovedAmount: number | null;
  ApprovedHours: number | null;
  DecisionNotes: string | null;
  DecidedByName: string | null;
  DecidedUtc: string | null;
  SubmittedUtc: string;
  CompletedOn: string | null;
}

export type TrainingRequestStatus = 'submitted' | 'approved' | 'denied';

/** A request as the Training Request page lists it. */
export interface TrainingRequest extends ApprovedTrainingRequest {
  EmployeeId: number;
  EmployeeName: string;
  EmployeeEmail: string | null;
  EmployeeRole: string | null;
  SupervisorEmployeeId: number | null;
  SupervisorName: string | null;
  SubmittedByName: string | null;
  Status: TrainingRequestStatus;
  Delivery: string | null;
  ExamIncluded: boolean;
  ExamName: string | null;
  ExamDate: string | null;
  Objective: string | null;
  BusinessUse: string | null;
  AlternativesNotes: string | null;
  KnowledgeShare: string | null;
  PaymentPreference: string | null;
  RenewalTerms: string | null;
  RegularHours: string | null;
  Coverage: string | null;
  OvernightTravel: boolean;
  Renewing: boolean;
  Repayment: boolean;
  OutsideBudget: string | null;
  Exception: boolean;
  ExceptionReason: string | null;
  DecidedByEmployeeId: number | null;
  /** Whether the person reading may approve or deny this one. */
  CanDecide: boolean;
}

export interface TrainingRequestLine {
  Kind: 'cost' | 'hours';
  Code: string;
  Label: string;
  Amount: number;
  Notes: string | null;
  SortOrder: number;
}

export interface TrainingRequestChoice {
  Field: string;
  Value: string;
}

export interface TrainingRequestList {
  requests: TrainingRequest[];
  /** Null where the person signed in has no employee record to queue against. */
  viewerEmployeeId: number | null;
  isAdmin: boolean;
}

export interface TrainingRequestDetail {
  request: TrainingRequest;
  lines: TrainingRequestLine[];
  choices: TrainingRequestChoice[];
}

export interface RecruitingRole {
  TrainingRoleId: number;
  Name: string;
  TemplateId: number | null;
  QuestionCount: number;
  CoreValueCount: number;
  CoreValuesTotal: number;
  /** The sample bank matching this role's name, named before committing to it. */
  SampleLabel: string;
  SampleMatched: boolean;
}

export interface RecruitingQuestion {
  RecruitingQuestionId: number;
  TrainingRoleId: number;
  Category: string;
  Text: string;
  /** What a strong answer sounds like — the part that makes two interviewers
   *  score the same answer alike. */
  Guidance: string | null;
  /** How to ask it. Standing, not per-candidate. */
  Note: string | null;
  Weight: 1 | 2 | 3;
  /** The V/TO value this question tests (migration 180). */
  CoreValueId: number | null;
  /** That value's name; or, for a question naming a value the V/TO does not
   *  have, the name it was given. */
  CoreValue: string | null;
  SortOrder: number;
  /** Which part of the process asks this. Null on every role that predates
   *  staged interviews, which is what makes those roles behave as before. */
  StageKey: string | null;
  /** Which competency this question informs, by its Ref. */
  CompetencyRef: string | null;
  /** Whose question it is on a panel — "L3 engineer", "Coordinator", "All". */
  Interviewer: string | null;
}

/** One of the company's core values, from the V/TO. */
export interface CoreValue {
  coreValueId: number;
  name: string;
  meaning: string;
}

/** One part of a staged interview process — what you pick before scoring. */
export interface RecruitingStage {
  StageId: number;
  StageKey: string;
  Name: string;
  Interviewers: string | null;
  Minutes: number | null;
  Focus: string | null;
  Output: string | null;
  /** This stage hands the candidate a brief beforehand. */
  HasBrief: boolean;
  SortOrder: number;
}

/**
 * What the hire is measured on. WeightPct is the competency's share of the
 * decision and sums to 100 across a role — a different axis from a question's
 * 1-3 Weight, which says how much that question matters within its stage.
 */
export interface RecruitingCompetency {
  CompetencyId: number;
  Ref: string;
  Name: string;
  WeightPct: number;
  GreatLooksLike: string | null;
  AssessedIn: string | null;
  /** Below 3 here is a no-hire whatever the weighted average says. */
  IsGate: boolean;
  SortOrder: number;
}

/** The handout a candidate is sent before a case stage. */
export interface RecruitingCaseBrief {
  Title: string;
  Intro: string | null;
  Body: { sections: { heading: string; rows: [string, string][] }[]; asks: string[] } | null;
  /** The interviewer's answer key. Never sent to the candidate. */
  InternalNote: string | null;
}

export interface RecruitingFramework {
  stages: RecruitingStage[];
  competencies: RecruitingCompetency[];
  brief: RecruitingCaseBrief | null;
}

export interface CandidateScore {
  ScoreId: number;
  RecruitingQuestionId: number | null;
  QuestionText: string;
  Category: string | null;
  /** The V/TO value, and the wording of it this score was given against. */
  CoreValueId: number | null;
  CoreValueVersionId: number | null;
  CoreValue: string | null;
  Weight: number;
  Rating: number;
  /** The evidence behind the rating — a quote, a number, an example. */
  Comment: string | null;
  /** Snapshotted with the rest, so a saved scorecard can still be read the
   *  way the decision was made. Null on every scorecard taken before staged
   *  interviews existed, and on every role without a competency model. */
  CompetencyRef: string | null;
  StageKey: string | null;
}

export interface Candidate {
  CandidateId: number;
  TrainingRoleId: number | null;
  RoleName: string | null;
  Name: string;
  Interviewer: string | null;
  Notes: string | null;
  ScoreTotal: number;
  ScoreMax: number;
  ScorePct: number;
  RatedCount: number;
  QuestionCount: number;
  InterviewedOn: string | null;
  CreatedUtc: string;
  /** Which part of the process this scorecard covers. Null for a role that
   *  has no stages, or where the answers spanned more than one. */
  StageKey: string | null;
  StageName: string | null;
}

export interface CandidateDetail extends Candidate {
  scores: CandidateScore[];
}

export interface JobDescriptionDoc {
  JobDescriptionId: number;
  Title: string;
  RoleId: number | null;
  /** Derived: `core.Role.Title` for `RoleId`. Read-only — set RoleId instead. */
  RoleName: string | null;
  TemplateId: number | null;
  TemplateName: string | null;
  Notes: string | null;
  FileName: string;
  MimeType: string;
  SizeBytes: number;
  UploadedUtc: string;
  /** Whether there is a readable version to open in the app. False for a PDF,
   *  a .doc, or a file the converter could not read — see migration 103. */
  HasHtml: boolean;
}

/** One job description, converted for reading. The file stays the master. */
export interface JobDescriptionView {
  Title: string;
  FileName: string;
  Html: string | null;
}

/* =========================================================================
   CS — Client Services. The CS Toolbox, moved in.
   ========================================================================= */

export type CsAuditType = 'internal' | 'client';
/** How often the audit recurs. Replaced planned/in_progress/complete — see
 *  migration 040: an audit is not a task that finishes. */
export type CsAuditFrequency = 'monthly' | 'quarterly' | 'biannual' | 'annual';

export interface CsAuditItem {
  CsAuditItemId: number;
  Position: number;
  ItemText: string;
  IsDone: boolean;
  /** When it was ticked. Null while outstanding. */
  CompletedUtc: string | null;
}

export interface CsAudit {
  CsAuditId: number;
  AuditType: CsAuditType;
  Title: string;
  /** Null for an internal audit, and for a client not in the directory yet. */
  ClientId: number | null;
  ClientName: string | null;
  /** Vestigial — the Scheduled field was removed from the form. Carried so an
   *  existing value survives an edit; nothing displays it. */
  AuditDate: string | null;
  /** The cadence it recurs on. */
  Frequency: CsAuditFrequency;
  Notes: string;
  OwnerEmployeeId: number | null;
  OwnerName: string | null;
  CreatedUtc: string;
  UpdatedUtc: string;
  Items: CsAuditItem[];
}

/**
 * A client's audit matrix, as account management reads it.
 *
 * Served by `GET /am/clients/:id/audit-matrix` — behind the AM card, not the CS
 * one, so an account manager can see what is being audited without being granted
 * the whole CS module. Item counts rather than the items themselves: this view
 * reports coverage, and the checklists are ticked in CS.
 */
export interface AmAuditMatrix {
  clientId: number;
  audits: {
    CsAuditId: number;
    Title: string;
    Frequency: CsAuditFrequency;
    Notes: string;
    OwnerEmployeeId: number | null;
    OwnerName: string | null;
    CreatedUtc: string;
    UpdatedUtc: string;
    ItemCount: number;
    DoneCount: number;
  }[];
}

export type CsTierKey = 'foundational' | 'standard' | 'advanced';
/** Which kind of audit a catalogue entry can be used for. */
export type CsApplicability = 'internal' | 'client' | 'both';

/** A catalogue entry. Data since migration 041 — it used to be a constant. */
export interface CsCatalogAudit {
  CsCatalogAuditId: number;
  /** Set on the ten that ship with the app, null on anything added here. */
  CatalogKey: string | null;
  Title: string;
  Blurb: string;
  Applicability: CsApplicability;
  Position: number;
  CreatedUtc: string;
  UpdatedUtc: string;
  /** Always all three keys; a tier that was never filled in is empty. */
  Tiers: Record<CsTierKey, { summary: string; items: string[] }>;
}

export interface CsAuditTemplate {
  CsAuditTemplateId: number;
  Name: string;
  AuditType: CsAuditType;
  Notes: string;
  CreatedUtc: string;
  Items: { CsAuditTemplateItemId: number; Position: number; ItemText: string }[];
}

export interface CsTool {
  CsToolId: number;
  Name: string;
  ToolUrl: string | null;
  PartnerUrl: string | null;
  Cost: string | null;
  SellPrice: string | null;
  PricingModel: string | null;
  AgreementExpires: string | null;
  Playbook: string;
  /** The bytes are never in the list payload — see TOOL_SELECT in routes/cs.ts. */
  HasLogo: boolean;
  LogoContentType: string | null;
  LogoFilename: string | null;
  CreatedUtc: string;
  UpdatedUtc: string;
}

export interface CsVendorContact {
  CsVendorContactId: number;
  Position: number;
  FullName: string;
  RoleTitle: string | null;
  Email: string | null;
  Phone: string | null;
}

export interface CsVendor {
  CsVendorId: number;
  VendorId: number;
  /** The vendor's name, joined from core.Vendor for display. */
  Company: string;
  PartnerUrl: string | null;
  TrainingUrl: string | null;
  BillingUrl: string | null;
  StatusUrl: string | null;
  RoadmapUrl: string | null;
  Notes: string;
  HasLogo: boolean;
  LogoContentType: string | null;
  LogoFilename: string | null;
  CreatedUtc: string;
  UpdatedUtc: string;
  Contacts: CsVendorContact[];
}

/** The shared company directory, as the audit client picker reads it. */
export interface CsClient {
  ClientId: number;
  Name: string;
  Status: string;
  IsActive: boolean;
}

export interface CsSummary {
  audits: { total: number; clientAudits: number; withOpenItems: number };
  tools: { total: number; withPlaybook: number };
  vendors: { total: number };
}

/* --- Projects -------------------------------------------------------------
   The Professional Services portal, moved in. See migration 043. */

export type ProjectStatus = 'active' | 'on_hold' | 'complete' | 'cancelled';
export type ProjectHealth = 'on_track' | 'at_risk' | 'needs_attention';

export interface Project {
  ProjectId: number;
  /** The number people quote — PS-1043 and up. Defaulted by the database. */
  Code: string;
  Name: string;
  ClientId: number | null;
  ClientName: string | null;
  ServiceLine: string;
  Status: ProjectStatus;
  Health: ProjectHealth;
  ManagerEmployeeId: number | null;
  ManagerName: string | null;
  EngineerEmployeeId: number | null;
  EngineerName: string | null;
  PercentComplete: number;
  BudgetHours: number;
  HoursUsed: number;
  StartDate: string | null;
  DueDate: string | null;
  Notes: string;
  /* Both decided by the server against the business calendar, never by the
     browser clock — see the note on PROJECT_SELECT in routes/projects.ts. */
  IsOverdue: boolean;
  DueInDays: number | null;
  CreatedUtc: string;
  UpdatedUtc: string;
}

export interface ProjectSummary {
  open: number;
  total: number;
  atRisk: number;
  overdue: number;
  dueSoon: number;
  /** Null when nothing is open — an average over no projects is not 0%. */
  avgCompletion: number | null;
  budgetHours: number;
  hoursUsed: number;
  /** Null when no hours are budgeted, for the same reason. */
  utilization: number | null;
}

export interface ProjectsConfig {
  /** Null hides the KPI tab — see config.projects in the API. */
  kpiDashboardUrl: string | null;
}

/* =========================================================================
   NOC — the alert knowledge base, moved in.

   Only the tile summary is here: the library's own payloads are read by exactly
   one page and are declared beside it in pages/noc/nocTypes.ts.
   ========================================================================= */

export interface NocSummary {
  alerts: number;
  categories: number;
  critical: number;
  /** Alerts carrying a parsed "action to be taken" section. */
  withRemediation: number;
  /** Whether the imported export had sites and hostnames scrubbed. */
  redacted: boolean;
  importedAt: string;
}

/* =========================================================================
   Admin — the operations admin dashboard, moved in.

   Only the tile summary is here. The module's own analytics payloads and
   register rows are read by exactly one page each and are declared beside them
   in pages/admin/adminTypes.ts — the same split the NOC module uses.
   ========================================================================= */

export interface AdminSummary {
  contracts: {
    total: number;
    expiring90: number;
    /** Contracts whose notice deadline falls inside the next 30 days. */
    noticeDeadline30: number;
    noticeMissed: number;
    clientMrr: number;
    valueAtRisk90: number;
  };
  procurement: { openPos: number; overdueReceipts: number; openPoValue: number };
  depot: { stockUnits: number; stockValue: number; rmaOpen: number };
  sops: { total: number; overdue: number };
  registrations: { total: number; lapsed: number; dueIn30: number };
  vendors: { total: number; reviewsOverdue: number };
  scorecard: {
    /** The last complete month — see lastCompletePeriod in the API. */
    period: string;
    measures: number;
    onTarget: number;
    atRisk: number;
    offTarget: number;
    noData: number;
    /** Null when nothing was measured, which is not the same as 0%. */
    weightedHealth: number | null;
  };
  /** Whether any phone data has been loaded at all. False is not an error —
   *  see the note on coo.CallMetric in migration 048. */
  hasCallData: boolean;
}

/* =========================================================================
   EOS — the Elliott Operating System, moved in.

   Only the tile summary is here. The module's own rows — teams, meetings,
   measurables, scores, templates, to-dos and headlines — are read by one page
   and are declared beside it in pages/eos/eosTypes.ts, the arrangement the
   NOC module uses.
   ========================================================================= */

export interface EosSummary {
  /** Null only when no EOS team exists yet — a fresh database, not an error. */
  teamId: number | null;
  teams: number;
  quarter?: string;
  rocks: { total: number; onTrack: number; offTrack: number; complete: number };
  scorecard: {
    measurables: number;
    /** How many of them have a number for `weekStart`. */
    scored: number;
    /** How many of those met their goal, read against each one's own rule. */
    hit: number;
    /** The Monday of the week the figures above describe — LAST week, because
     *  that is the one reported in this week's meeting. */
    weekStart: string;
  };
  todos: { open: number; overdue: number };
  issues: { openShort: number; openLong: number };
  lastMeeting: {
    meetingId: number;
    title: string;
    date: string;
    averageRating: number | null;
  } | null;
  nextMeeting: { meetingId: number; title: string; date: string; status: string } | null;
}

/* =========================================================================
   The generated business review — GET /reviews/business-review

   Read-only and computed on request; there is no row behind it. Field names
   are PascalCase because they come straight off the SQL projection, matching
   every other shape in this file.
   ========================================================================= */

/** How an item came to be filed under a unit — see routes/reviews.ts. */
export type AttributedBy = 'tag' | 'meeting' | 'owner' | null;

interface Attributed {
  ResolvedUnitId: number | null;
  AttributedBy: AttributedBy;
}

export interface ReviewRock extends Attributed {
  RockId: number;
  Title: string;
  Status: string;
  Quarter: string | null;
  DueDate: string | null;
  IsCompanyRock: boolean;
  OwnerName: string | null;
  /** Unfinished and from a quarter before this one. */
  CarriedOver: boolean;
}

export interface ReviewIssue extends Attributed {
  IssueId: number;
  Title: string;
  Term: string;
  Priority: string;
  Status: string;
  RaisedUtc: string;
  OwnerName: string | null;
}

export interface ReviewTodo extends Attributed {
  TodoId: number;
  Title: string;
  DueDate: string | null;
  TeamName: string;
  OwnerName: string | null;
  Overdue: boolean;
}

export interface ReviewMeasurable extends Attributed {
  MeasurableId: number;
  Name: string;
  Unit: string;
  Goal: number;
  Comparison: string;
  TeamName: string;
  OwnerName: string | null;
  /** Last completed week's number. Null when nobody entered one. */
  LastValue: number | null;
  LastHit: boolean;
  Scored13: number;
  Hit13: number | null;
}

export interface ReviewUnit {
  BusinessUnitId: number;
  Name: string;
  Description: string | null;
  SortOrder: number;
  /** Active people assigned to this unit. Zero means nothing derives into it. */
  EmployeeCount: number;
  /** The department the unit sits under. Not what `Oml` below is scored for
   *  any more — the area is — but still what the unit is a part of. */
  DepartmentId: number | null;
  DepartmentName: string | null;
  /** The business area this unit's work belongs to, which is how it reaches a
   *  maturity score: the OML section naming the same area is the one scored
   *  for it. Null means no score, and the page says so rather than printing a
   *  bare em dash — it is set on the Business Units screen. */
  AreaId: number | null;
  AreaName: string | null;
  /** Active units on this area, itself included. One is the ordinary case.
   *  More means they read one section's score between them, which the page
   *  discloses beside the figure. Zero where the unit has no area. */
  AreaUnits: number;
  /** The maturity of the OML section about this unit's work. Null where the
   *  unit has no area, or where no section of the assessment scores it. */
  Oml: {
    Score: number | null;
    Scored: number;
    Title: string;
    PeriodLabel: string | null;
    ConductedOn: string | null;
    /** The section the figure is about. */
    SectionName: string;
    /** Sections averaged into it. More than one means two sections name this
     *  area and this is their mean, which the page discloses. */
    Sections: number;
  } | null;
  Rocks: ReviewRock[];
  Issues: ReviewIssue[];
  Todos: ReviewTodo[];
  Measurables: ReviewMeasurable[];
}

export interface GeneratedReview {
  GeneratedUtc: string;
  Quarter: string;
  WeekStart: string;
  Window13Start: string;
  Units: ReviewUnit[];
  Unattributed: {
    Rocks: ReviewRock[];
    Issues: ReviewIssue[];
    Todos: ReviewTodo[];
    Measurables: ReviewMeasurable[];
  };
}

/* --- Processes (Settings) -------------------------------------------------
   What the business does, who is accountable for it, which business areas run
   it and what says it is working. Brought across from the standalone EOS app —
   see migrations 064-066.
   -------------------------------------------------------------------------- */

/** A process is filed under a business AREA, not a department — see 065. */
export interface ProcessArea {
  AreaId: number;
  Code: string;
  Name: string;
}

export interface ProcessMeasurable {
  ProcessMeasurableId: number;
  /** The library item it was taken from, or null once that has gone. The form
   *  keeps showing those so they can be cleared deliberately rather than
   *  vanishing from the card. */
  ScorecardItemId: number | null;
  Name: string;
  Unit: MeasurableUnit;
  Goal: number | null;
  Comparison: MeasurableComparison;
}

export type MeasurableUnit = '#' | '$' | '%';
export type MeasurableComparison = '>=' | '<=' | '=';

export interface Process {
  ProcessId: number;
  Title: string;
  Description: string | null;
  OwnerEmployeeId: number | null;
  OwnerName: string | null;
  /** The OML level at which a business of this maturity should be running it.
   *  Null means unrated, which the page filters on separately from level 1. */
  MaturityLevel: number | null;
  CreatedUtc: string;
  ModifiedUtc: string;
  areas: ProcessArea[];
  measurables: ProcessMeasurable[];
}

/**
 * One row of the scorecard item library — Settings > Scorecard Items.
 *
 * The list a process is measured out of, and the same rows the process form
 * picks from. Seeded from the EOS app in migration 098.
 */
export interface ScorecardItem {
  ScorecardItemId: number;
  /** The prefix the EOS app kept inside the title. Null for the 27 items that
   *  never had one — a real state, not a gap. */
  Category: string | null;
  Name: string;
  BusinessUnitId: number | null;
  BusinessUnitName: string | null;
  Description: string | null;
  Unit: MeasurableUnit;
  Goal: number | null;
  Comparison: MeasurableComparison;
  Cadence: ScorecardCadence | null;
  IsActive: boolean;
  SortOrder: number;
  /** How many processes have attached it. Renaming one that fourteen
   *  processes point at is a different decision from renaming an unused one. */
  ProcessCount: number;
  CreatedUtc: string;
  ModifiedUtc: string;
}

export type ScorecardCadence = 'weekly' | 'monthly' | 'quarterly' | 'annual';

/** A measurable the form can attach: an active row of the library. */
export interface ProcessMeasurableOption {
  ScorecardItemId: number;
  Category: string | null;
  Name: string;
  Description: string | null;
  Unit: MeasurableUnit;
  Goal: number | null;
  Comparison: MeasurableComparison;
  Cadence: ScorecardCadence | null;
}

/**
 * Every scorecard for one candidate on one role, pooled.
 *
 * The answers are returned rather than a total: the combined view runs the
 * same weighting the live screen runs, which is what stops it disagreeing
 * with the parts it is made of.
 */
export interface CombinedScorecard {
  name: string;
  cards: Candidate[];
  answers: (CandidateScore & { CandidateId: number })[];
}

/* =========================================================================
   Change requests
   ========================================================================= */

export type ChangeKind = 'process' | 'tool';
export type ChangeStatus = 'draft' | 'submitted' | 'approved' | 'declined' | 'withdrawn';
export type ChangePriority = 'low' | 'medium' | 'high' | 'critical';
export type ChangeImpact = 'low' | 'medium' | 'high';
export type ChangeDecision = 'approved' | 'approved_with_conditions' | 'deferred' | 'declined';

/**
 * A proposed change to a process, or a request for a new tool.
 *
 * The tool block is null for a process change by definition — the API clears
 * it rather than trusting the caller, so `Kind` is the only thing to check.
 */
export interface ChangeRequest {
  ChangeRequestId: number;
  Kind: ChangeKind;
  Title: string;
  Status: ChangeStatus;
  Priority: ChangePriority;
  RequestedByEmployeeId: number | null;
  RequestedByName: string | null;

  Problem: string | null;
  Proposal: string | null;
  Justification: string | null;
  WhoIsAffected: string | null;
  ImpactLevel: ChangeImpact;
  RiskAndMitigation: string | null;
  RollbackPlan: string | null;
  SuccessMeasure: string | null;
  TargetDate: string | null;

  ToolName: string | null;
  Vendor: string | null;
  AnnualCost: number | null;
  Replaces: string | null;
  DataHandled: string | null;

  Decision: ChangeDecision | null;
  Conditions: string | null;
  DecisionNote: string | null;
  DecidedByEmployeeId: number | null;
  DecidedByName: string | null;
  DecidedOn: string | null;

  CreatedUtc: string;
  ModifiedUtc: string;
}
